import React, { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/context/AuthContext";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";

const ROLES = [
  { value: "admin", label: "Admin / Owner" },
  { value: "kasir", label: "Kasir / Resepsionis" },
  { value: "designer", label: "Designer / Digitizer" },
  { value: "operator", label: "Operator Produksi" },
];

export default function UsersPage() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "kasir" });
  const [del, setDel] = useState(null);

  const load = useCallback(() => { api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEdit(null); setForm({ name: "", email: "", password: "", role: "kasir" }); setOpen(true); };
  const openEdit = (u) => { setEdit(u); setForm({ name: u.name, email: u.email, password: "", role: u.role }); setOpen(true); };

  const save = async () => {
    try {
      if (edit) { await api.put(`/users/${edit.id}`, { name: form.name, role: form.role, password: form.password || undefined }); toast.success("Pengguna diperbarui"); }
      else { await api.post("/users", form); toast.success("Pengguna dibuat"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const doDelete = async () => {
    try { await api.delete(`/users/${del.id}`); toast.success("Pengguna dihapus"); load(); }
    catch (e) { toast.error(apiErr(e)); }
    setDel(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl">Manajemen Pengguna</h1>
          <p className="text-sm text-slate-500">Kelola akun staf dan hak akses peran.</p>
        </div>
        <Button data-testid="btn-add-user" className="bg-blue-600 hover:bg-blue-700" onClick={openNew}><Plus className="mr-2 h-4 w-4" /> Tambah Pengguna</Button>
      </div>

      <Card className="border-slate-200/80 shadow-sm overflow-x-auto">
        <Table data-testid="table-users">
          <TableHeader><TableRow className="bg-slate-50/50">
            <TableHead>Nama</TableHead><TableHead>Email</TableHead><TableHead>Peran</TableHead><TableHead>Dibuat</TableHead><TableHead className="text-right">Aksi</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id}>
                <TableCell className="font-semibold text-slate-800">{u.name}</TableCell>
                <TableCell className="text-sm text-slate-600">{u.email}</TableCell>
                <TableCell><StatusBadge value={u.role} kind="role" className="capitalize" /></TableCell>
                <TableCell className="text-sm text-slate-500">{formatDate(u.created_at, true)}</TableCell>
                <TableCell className="text-right">
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(u)}><Pencil className="h-4 w-4" /></Button>
                  {u.id !== me?.id && <Button data-testid={`del-user-${u.email}`} variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => setDel(u)}><Trash2 className="h-4 w-4" /></Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{edit ? "Edit Pengguna" : "Tambah Pengguna"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Nama</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Email</Label><Input data-testid="user-email" type="email" value={form.email} disabled={!!edit} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>{edit ? "Kata Sandi Baru (opsional)" : "Kata Sandi"}</Label><Input data-testid="user-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Peran</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button data-testid="btn-save-user" className="bg-blue-600 hover:bg-blue-700" onClick={save}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!del} onOpenChange={(v) => !v && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Hapus {del?.name}?</AlertDialogTitle><AlertDialogDescription>Akun ini akan dihapus permanen.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction data-testid="btn-confirm-delete-user" className="bg-rose-600 hover:bg-rose-700" onClick={doDelete}>Hapus</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
